/**
 * Top-nav restore pill for the Get Started widget.
 *
 * Only renders when the bottom-right widget is in `minimized` state. Clicking
 * the pill returns the widget to the `expanded` state — bottom-right panel
 * reappears, this pill hides itself. Matches Stripe's Setup-guide pattern.
 *
 * Displays a small circular progress ring inside the pill reflecting how
 * far through the checklist the user is, plus a hover tooltip prompting
 * them to reopen the guide.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@scio3/ui';
import { cn } from '@/lib/utils';
import { useGetStartedChecklist } from '@/features/get-started/hooks/useGetStartedChecklist';
import { useGetStartedWidgetStore } from '@/features/get-started/stores/get-started-widget-store';

/**
 * Inline SVG progress ring sized to match the pill's text — small, crisp,
 * always renders even at 0% so the ring outline is visible. Uses semantic
 * tokens so it tracks the design system in light + dark mode.
 */
function ProgressRing({ percent }: { percent: number }) {
  // Geometry — viewBox is 20×20, ring is a 7-radius stroke-2.5 circle.
  // strokeDasharray = circumference; strokeDashoffset = circumference *
  // (1 - percent/100) hides the unfilled portion.
  const radius = 7;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - Math.max(0, Math.min(100, percent)) / 100);

  return (
    <svg
      // 22×22 px — taller than the pill's text (sm = ~20px line-height)
      // so the ring reads as the dominant glyph on the pill, matching
      // Stripe's Setup-guide reference. ! suffix beats the Button
      // variant's [&_svg]:size-4 (16px default) — we explicitly want a
      // larger glyph here.
      width="22"
      height="22"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
      className="size-[22px]! shrink-0"
    >
      {/* Background ring — full circle, light gray. Explicit semantic
			    token (not currentColor) so the ring color is decoupled from
			    the pill's text color. */}
      <circle
        cx="10"
        cy="10"
        r={radius}
        strokeWidth="2.5"
        fill="none"
        className="stroke-muted-foreground/30"
      />
      {/* Progress arc — primary green, rotated -90° so it starts at
			    the top (12 o'clock) and fills clockwise. */}
      <circle
        cx="10"
        cy="10"
        r={radius}
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform="rotate(-90 10 10)"
        className="stroke-primary transition-[stroke-dashoffset] duration-500 ease-out"
      />
    </svg>
  );
}

type Props = {
  /**
   * When true, render the pill as an icon-only progress ring (no "Get
   * Started" text label). Used at narrow nav widths where the full pill
   * would push the right cluster into the primary nav items. The progress
   * ring + tooltip keep the affordance discoverable while saving ~80px of
   * horizontal space.
   */
  compact?: boolean;
};

export function GetStartedPill({ compact = false }: Props) {
  const { t } = useTranslation();
  const restoreFromPill = useGetStartedWidgetStore((s) => s.restoreFromPill);
  const justMinimized = useGetStartedWidgetStore((s) => s.justMinimized);
  const clearJustMinimized = useGetStartedWidgetStore((s) => s.clearJustMinimized);
  // Same hook the bottom-right widget uses — single source of truth so the
  // ring always reflects the user's real progress without drift.
  const { state } = useGetStartedChecklist();
  // Snapshot the just-minimized flag at mount time. The pill only mounts
  // when widgetState flips to 'minimized', so this is true exactly when
  // the user just clicked X. On a page reload that started with stored
  // widgetState='minimized', the flag is false → pill renders statically
  // with no spurious entrance animation (the user didn't just do anything).
  const shouldAnimateInRef = useRef(justMinimized);

  // Clear the flag after the first render so the next minimize cycle gets
  // a fresh signal. Safe to call in effect — the ref captured the value
  // at mount, so reading from store after clearing won't reanimate.
  useEffect(() => {
    if (shouldAnimateInRef.current) {
      clearJustMinimized();
    }
  }, [clearJustMinimized]);

  // NOTE: Parent (TopNavigation) conditionally mounts this component based
  // on `widgetState === 'minimized'` — we don't early-return null here
  // because that would prevent the component from remounting on each
  // minimize, which is what makes `useRef(justMinimized)` capture the
  // fresh action flag correctly. If you wrap this component somewhere
  // new, gate the mount in the parent, not inside.

  return (
    // Local provider — TopNavigation doesn't wrap children in a global
    // TooltipProvider; each feature mounts its own (project convention).
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid="get-started-restore-pill"
            size="sm"
            // `variant="ghost"` gives a transparent base — we paint our
            // own neutral pill on top so brand-green is reserved for
            // the progress arc (closer to Stripe's Setup-guide pill
            // style than `default` variant's saturated primary bg).
            // `bg-panel-hover` is the one step darker than the nav's
            // `bg-muted` background so the pill is visibly raised
            // instead of blending in. `bg-input` on hover is another
            // step darker for a clear interactive state.
            variant="ghost"
            className={cn(
              // `hover:bg-...!` important suffix — the ghost variant adds
              // `hover:bg-panel-hover` of its own, and tailwind-merge
              // doesn't dedupe `bg-panel-hover` (custom utility) against
              // `bg-muted-foreground/40` (Tailwind-standard utility), so
              // both hover classes end up on the element and cascade
              // order picks panel-hover. `!` forces our hover to win.
              // Resting bg slightly darker than the nav's `bg-muted` so
              // the pill is visibly raised. `bg-muted-foreground/10` is
              // a semi-transparent dark-gray tint over the nav bg —
              // calm-neutral, lets the green progress arc be the only
              // brand-color on the pill.
              'shrink-0 cursor-pointer rounded-full bg-muted-foreground/10 text-foreground hover:bg-muted-foreground/25!',
              // Layout differs by mode:
              //   full    — gap between text + ring; trimmed right padding so
              //             the rounded-full curve hugs the ring (no gap).
              //   compact — square padding around just the ring → a circular
              //             ~34px icon button matching `size-sm` (32px) +
              //             the 22px ring inset.
              compact ? 'px-1.5' : 'gap-1.5 pr-1.5',
              // Animate ONLY on a fresh minimize. On page reloads where
              // widgetState was restored from localStorage already as
              // 'minimized', the snapshot is false → pill renders static.
              shouldAnimateInRef.current && 'animate-get-started-pill-in',
            )}
            onClick={restoreFromPill}
            aria-label={t('getStarted.restoreFromPill')}
          >
            {!compact && t('Get Started')}
            {compact ? (
              // Compact mode: a filled brand-green star — matches the icon
              // used by GetStartedDropdown (the legacy entry point) so the
              // Get Started feature has a consistent visual identity across
              // surfaces. The bare progress ring read as an abstract target
              // reticle at small size; the star is unambiguously a "feature
              // marker" and survives without the "Get Started" text label.
              <Star className="size-[18px]! shrink-0 fill-primary text-primary" aria-hidden />
            ) : (
              <ProgressRing percent={state.progressPercent} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('getStarted.restoreFromPill')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
